import { NavLink, useNavigate } from "react-router-dom";
import React, { useState, useRef, useEffect } from "react";
import { Bell, User, Menu, X } from "lucide-react";
import style from "../style/header.module.css";
import { useAuthStore } from "../hooks/useAuthStore";
import NotificationDrawer from "./NotificationDrawer";
import { useAppConfigContext } from "../context/AppConfigContext";

function Header() {
  const { config, isServiceEnabled } = useAppConfigContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const profileMenuRef = useRef(null);
  const profileButtonRef = useRef(null);
  const navigate = useNavigate();

  const { isAuthenticated, userName, role, actions } = useAuthStore();

  const toggleMenu = () => {
    setIsMenuOpen((prevState) => !prevState);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen((prevState) => !prevState);
  };

  const closeMenu = (event) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target) &&
      !(menuButtonRef.current && menuButtonRef.current.contains(event.target))
    ) {
      setIsMenuOpen(false);
    }

    if (
      profileMenuRef.current &&
      !profileMenuRef.current.contains(event.target) &&
      !(
        profileButtonRef.current &&
        profileButtonRef.current.contains(event.target)
      )
    ) {
      setIsProfileMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", closeMenu);
    return () => {
      document.removeEventListener("mousedown", closeMenu);
    };
  }, []);

  const getDashboardPath = () => {
    if (role === "admin" || role === "super_admin") return "/admin";
    if (role === "assistant" || role === "serviceProvider") return "/dashboards/assistant/overview";
    return "/user/travel-assistant";
  };

  const handleLogout = async () => {
    await actions.logout();
    setIsProfileMenuOpen(false);
    navigate("/auth/login", { replace: true });
  };

  return (
    <div className="sticky inset-x-0 top-0 z-50 flex items-center justify-between bg-white px-6 py-2 ">
      {/* Logo Section */}

      <div className={style.logoH}>
        <NavLink to={isAuthenticated ? getDashboardPath() : "/"}>
          <img
            src={config.logoUrl || "/images/logo.png"}
            alt={config.appName || "Logo"}
            className={style.logo}
          />
        </NavLink>
      </div>

      {/* Navigation Menu */}
      <nav
        ref={menuRef}
        id="mobile-navigation"
        className={`items-center gap-5 ${
          isMenuOpen
            ? "fixed left-0 top-16 flex w-full flex-col rounded-b-2xl bg-white px-6 py-4 shadow-md md:static md:w-auto md:flex-row md:bg-transparent md:p-0 md:shadow-none"
            : "hidden md:flex"
        }`}
      >
        <ul className="text-md flex flex-col items-center gap-4 md:flex-row md:gap-5">
          {!isAuthenticated && (
            <>
              <li>
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `relative font-medium transition-colors after:absolute after:left-0 after:-bottom-[2px] after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-[#570033] after:transition-transform hover:text-[#570033] hover:after:scale-x-100 ${
                      isActive
                        ? "text-[#570033] after:scale-x-100"
                        : "text-[#2B333C]"
                    }`
                  }
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </NavLink>
              </li>

              {isServiceEnabled("puloAssistants") && (
                <li>
                  <NavLink
                    to="/travel-assistant"
                    className={({ isActive }) =>
                      `relative font-medium transition-colors after:absolute after:left-0 after:-bottom-[2px] after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-[#570033] after:transition-transform hover:text-[#570033] hover:after:scale-x-100 ${
                        isActive
                          ? "text-[#570033] after:scale-x-100"
                          : "text-[#2B333C]"
                      }`
                    }
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Pulo Assistant
                  </NavLink>
                </li>
              )}

              {isServiceEnabled("stays") && (
                <li>
                  <NavLink
                    to="/stays"
                    className={({ isActive }) =>
                      `relative font-medium transition-colors after:absolute after:left-0 after:-bottom-[2px] after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-[#570033] after:transition-transform hover:text-[#570033] hover:after:scale-x-100 ${
                        isActive
                          ? "text-[#570033] after:scale-x-100"
                          : "text-[#2B333C]"
                      }`
                    }
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Stay
                  </NavLink>
                </li>
              )}

              {isServiceEnabled("rides") && (
                <li>
                  <NavLink
                    to="/rides"
                    className={({ isActive }) =>
                      `relative  font-medium transition-colors after:absolute after:left-0 after:-bottom-[2px] after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-[#570033] after:transition-transform hover:text-[#570033] hover:after:scale-x-100 ${
                        isActive
                          ? "text-[#570033] after:scale-x-100"
                          : "text-[#2B333C]"
                      }`
                    }
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Rides
                  </NavLink>
                </li>
              )}
            </>
          )}

          {!isAuthenticated && (
            <>
              <li>
                <NavLink
                  to="/about"
                  className={({ isActive }) =>
                    `relative font-medium transition-colors after:absolute after:left-0 after:-bottom-[2px] after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-[#570033] after:transition-transform hover:text-[#570033] hover:after:scale-x-100 ${
                      isActive
                        ? "text-[#570033] after:scale-x-100"
                        : "text-[#2B333C]"
                    }`
                  }
                  onClick={() => setIsMenuOpen(false)}
                >
                  About us
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/contact"
                  className={({ isActive }) =>
                    `relative font-medium transition-colors after:absolute after:left-0 after:-bottom-[2px] after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-[#570033] after:transition-transform hover:text-[#570033] hover:after:scale-x-100 ${
                      isActive
                        ? "text-[#570033] after:scale-x-100"
                        : "text-[#2B333C]"
                    }`
                  }
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact us
                </NavLink>
              </li>
            </>
          )}
        </ul>
      </nav>

      {/* Always Visible Section */}
      <div className="flex items-center gap-4 font-semibold">
        <button
          type="button"
          onClick={() => setIsNotificationsOpen(true)}
          aria-label="Notifications"
          className="relative flex items-center justify-center rounded-full bg-[#FAF7FA] p-2 text-[#570033] shadow-sm transition hover:shadow"
        >
          <Bell />
        </button>

        <div className="relative">
          <button
            type="button"
            ref={profileButtonRef}
            onClick={toggleProfileMenu}
            aria-haspopup="true"
            aria-expanded={isProfileMenuOpen}
            className="flex justify-center items-center rounded-full bg-[#FAF7FA] p-2 text-[#570033] shadow-sm transition hover:shadow"
          >
            <User />
          </button>

          {isProfileMenuOpen && (
            <div
              ref={profileMenuRef}
              className="space-y-3 absolute right-0 mt-3 py-6 px-4 w-44 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-lg ring-1 ring-black/5"
            >
              {isAuthenticated ? (
                <>
                  <div className="px-2 text-sm font-medium text-gray-500">
                    {userName || "Signed in"}
                  </div>
                  <NavLink
                    to={getDashboardPath()}
                    className="block px-2 py-2 text-md rounded-lg font-medium text-gray-800 transition hover:bg-[#FAF7FA] hover:text-[#61013B]"
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    Dashboard
                  </NavLink>
                  <button
                    type="button"
                    className="w-full text-left px-2 py-2 text-md rounded-lg font-medium text-gray-800 transition hover:bg-[#FAF7FA] hover:text-[#61013B]"
                    onClick={handleLogout}
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <NavLink
                    to="/auth/login"
                    className="block px-2 py-2 text-md rounded-lg font-medium text-gray-800 transition hover:bg-[#FAF7FA] hover:text-[#61013B]"
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    Log in
                  </NavLink>
                  <NavLink
                    to="/auth/welcome"
                    className="block px-2 py-2 text-md rounded-lg font-medium text-gray-800 transition hover:bg-[#FAF7FA] hover:text-[#61013B]"
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    Sign up
                  </NavLink>
                </>
              )}
            </div>
          )}
        </div>

        {/* Hamburger Menu */}
        <button
          ref={menuButtonRef}
          className="flex h-10 w-10 items-center justify-center rounded-md p-2 text-[#570033] md:hidden"
          onClick={toggleMenu}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-navigation"
          aria-label="Toggle navigation menu"
        >
          {isMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </div>

      <NotificationDrawer
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
      />
    </div>
  );
}

export default Header;
